import { useCallback, useEffect, useRef, useState } from 'react';
import { datasetsApi } from '../lib/api';
import { startTileAuth } from '../lib/map-auth';
import {
  chooseLayerMode, tileKinds, unionBounds, vtApi, vtLayerSpec, vtPaint, vtPopupProps,
} from '../lib/vector-tiles';

/**
 * Vector-tile layers for a large dataset opened with /map?dataset=<id>
 * (docs/VECTOR_TILES.md). Active only when `chooseLayerMode(metadata) === 'vector'`;
 * smaller datasets stay with hooks/useDatasetRawLayers (GeoJSON). `&tiles=pmtiles`
 * reads the exported PMTiles archive instead of the live MVT endpoint.
 *
 * Renders through components/map/VectorTileLayers.js; clicks are handled here with
 * queryRenderedFeatures so MapView's selection logic is untouched.
 *
 * status: 'idle' | 'loading' | 'ready' | 'error'
 */

/** The first rendered feature that belongs to one of our layers, as popup info. */
export function pickVectorFeature(features, specs, lngLat) {
  const byLayer = new Map(specs.map(s => [s.layerId, s]));
  const f = (features || []).find(x => byLayer.has(x?.layer?.id));
  if (!f) return null;
  const spec = byLayer.get(f.layer.id);
  const coords = f.geometry?.type === 'Point' ? f.geometry.coordinates : [lngLat?.lng, lngLat?.lat];
  return {
    longitude: coords[0], latitude: coords[1], kind: spec.kind, title: spec.name,
    rows: vtPopupProps(spec.kind, f.properties || {}),
  };
}

function getMap(mapRefHolder) {
  return mapRefHolder?.current?.current?.getMap?.() || null;
}

/** Run fn(map) once the map instance exists (polls briefly, like useDatasetRawLayers). */
function whenMap(mapRefHolder, fn) {
  let tries = 0;
  const t = setInterval(() => {
    const map = getMap(mapRefHolder);
    if (map) { clearInterval(t); fn(map); } else if (++tries > 40) clearInterval(t);
  }, 150);
  return () => clearInterval(t);
}

export function useVectorTileLayers({ searchParams, mapRefHolder }) {
  const datasetId = searchParams.get('dataset');
  const usePmtiles = searchParams.get('tiles') === 'pmtiles';
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [layers, setLayers] = useState([]);
  const [popup, setPopup] = useState(null);
  const tileJsons = useRef({});

  useEffect(() => {
    setLayers([]);
    setPopup(null);
    setError(null);
    if (!datasetId) { setStatus('idle'); return undefined; }
    let active = true;
    let stopFit = () => {};
    (async () => {
      const res = await datasetsApi.get(datasetId);
      const ds = res.data || {};
      if (chooseLayerMode(ds.metadata) !== 'vector') { if (active) setStatus('idle'); return; }
      setStatus('loading');
      // Lazy: keeps supabase-js and pmtiles out of this module's import graph (tests, bundle).
      const { supabase } = await import('../lib/supabase');
      await startTileAuth(supabase); // tile requests need the bearer token before the first fetch
      if (usePmtiles) (await import('../lib/pmtiles-protocol')).ensurePmtilesProtocol();
      const specs = [];
      for (const kind of tileKinds(ds.metadata)) {
        const tj = (await vtApi.tileJson(datasetId, kind)).data;
        tileJsons.current[kind] = tj;
        let pmtilesUrl = null;
        if (usePmtiles) {
          try { pmtilesUrl = (await vtApi.pmtilesUrl(datasetId, kind)).data?.url || null; } catch { /* live tiles */ }
        }
        specs.push(vtLayerSpec(datasetId, kind, tj, { name: ds.name, pmtilesUrl }));
      }
      if (!active) return;
      setLayers(specs);
      setStatus('ready');
      const bounds = unionBounds(specs);
      if (bounds) {
        stopFit = whenMap(mapRefHolder, (map) => map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 18 }));
      }
    })().catch((e) => {
      if (!active) return;
      setError(e?.response?.data?.detail || e?.message || 'Could not load the vector tiles');
      setStatus('error');
    });
    return () => { active = false; stopFit(); };
  }, [datasetId, usePmtiles, mapRefHolder]);

  // Click popups: query our layers only.
  useEffect(() => {
    if (!layers.length) return undefined;
    let map = null;
    const onClick = (evt) => {
      const ids = layers.filter(l => l.visible && map.getLayer(l.layerId)).map(l => l.layerId);
      if (!ids.length) return;
      const feats = map.queryRenderedFeatures(evt.point, { layers: ids });
      const info = pickVectorFeature(feats, layers, evt.lngLat);
      if (info) setPopup(info);
    };
    const stop = whenMap(mapRefHolder, (m) => { map = m; map.on('click', onClick); });
    return () => { stop(); if (map) map.off('click', onClick); };
  }, [layers, mapRefHolder]);

  const toggleLayer = useCallback((id) => {
    setLayers(prev => prev.map(l => (l.id === id ? { ...l, visible: !l.visible } : l)));
  }, []);

  const setZField = useCallback((id, field) => {
    setLayers(prev => prev.map(l => (l.id === id
      ? { ...l, zField: field, paint: vtPaint(l.kind, tileJsons.current[l.kind], { zField: field }) } : l)));
  }, []);

  const closePopup = useCallback(() => setPopup(null), []);

  return { datasetId, status, error, layers, popup, closePopup, toggleLayer, setZField, usePmtiles };
}
